import { HTMLAttributes, ReactNode, useCallback, useEffect, useRef } from "react";
import { transitionClassName } from "./commonClassNames";

type DropdownMenuProps = HTMLAttributes<HTMLDivElement> & {
  isOpen?: boolean;
  right?: boolean;
  fixed?: boolean;
  dropdownMenuClass?: string;
  className?: string;
};

export type DropdownProps = HTMLAttributes<HTMLDivElement> &
  Pick<DropdownMenuProps, "isOpen" | "right" | "fixed"> & {
    trigger?: ReactNode;
    setIsOpen?: (isOpen: boolean) => void;
    dropdownMenuClass?: string;
    className?: string;
  };

export type DropdownItemProps = HTMLAttributes<HTMLDivElement>;

type DropdownTriggerProps = HTMLAttributes<HTMLDivElement>;

export const Dropdown: React.FC<DropdownProps> = ({
  children,
  isOpen,
  setIsOpen,
  trigger,
  right,
  fixed,
  className,
  dropdownMenuClass,
  ...props
}) => {
  const combinedClassName = "relative inline-block text-left outline-0";

  const containerRef = useRef<HTMLDivElement>(null);

  const toggleMenu = useCallback(() => {
    setIsOpen?.(!isOpen);
  }, [setIsOpen, isOpen]);

  const closeMenuOnOutsideClick = useCallback(
    (event: MouseEvent) => {
      const eventIsInside = containerRef.current?.contains(event.target as Node);
      if (!eventIsInside) {
        setIsOpen?.(false);
      }
    },
    [setIsOpen],
  );

  useEffect(() => {
    if (!containerRef.current) return;

    window.addEventListener("click", closeMenuOnOutsideClick);

    return () => {
      window.removeEventListener("click", closeMenuOnOutsideClick);
    };
  }, [closeMenuOnOutsideClick]);

  return (
    <div ref={containerRef} {...props} className={combinedClassName}>
      {trigger ? <DropdownTrigger onClick={toggleMenu}>{trigger}</DropdownTrigger> : null}

      <DropdownMenu
        dropdownMenuClass={dropdownMenuClass}
        isOpen={isOpen}
        right={right}
        fixed={fixed}
        className={className}
      >
        {children}
      </DropdownMenu>
    </div>
  );
};

export const DropdownItem: React.FC<DropdownItemProps> = ({ children, ...props }) => {
  const className = [
    "relative inline-flex items-center gap-2 text-left text-xs font-medium select-none min-w-max w-full outline-0 px-3 py-3",
    "text-black hover:text-primary-200 hover:bg-primary-100 dark:text-white dark:bg-primary-500 hover:dark:text-primary-400 hover:dark:bg-primary-700",
  ].join(" ");

  return (
    <div {...props} className={className}>
      {children}
    </div>
  );
};

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  isOpen,
  right,
  fixed,
  dropdownMenuClass,
  className,
  ...props
}) => {
  const combinedClassName = [
    `${fixed ? "fixed" : "absolute"} rounded-md z-10`,
    "bg-white dark:bg-primary-500",
    right ? "right-0" : "",
    isOpen
      ? "visible transform opacity-100 shadow-lg transform-x"
      : "invisible transform opacity-0 translate-y-1 shadow-none",
    dropdownMenuClass,
    transitionClassName({ all: true }),
    className ?? "",
  ].join(" ");

  return (
    <div {...props} className={combinedClassName}>
      {children}
    </div>
  );
};

const DropdownTrigger: React.FC<DropdownTriggerProps> = ({ children, ...props }) => {
  const className = [
    "inline-flex items-center gap-2 font-bold select-none",
    "text-neutral-600 dark:text-white hover:text-primary-200 hover:dark:text-primary-400",
    transitionClassName({ colors: true }),
  ].join(" ");

  return (
    <div {...props} className={className}>
      {children}
    </div>
  );
};
